import moment from "moment";
import { ContextMenuDirections, Position } from "../hooks/useOpener";
import { v4 as uuidv4 } from "uuid";
import { env } from "env";

export const redirectToUrlWithPostRequest = (url: string) => {
  const form = document.createElement("form");
  form.method = "post";
  form.action = url;

  form.style.display = "none";
  document.body.appendChild(form);

  form.submit();
};

export const getBlobUrl = (blob: Blob) => {
  const url = window.URL || window.webkitURL;
  const link = url.createObjectURL(blob);
  return link;
};

export const downloadBlob = (blob: Blob, fileName: string = "") => {
  const link = getBlobUrl(blob);
  downloadByUrl(link, fileName);
};

export const downloadByUrl = (link: string, fileName: string = "") => {
  const a = document.createElement("a");
  a.setAttribute("download", fileName || "file");
  a.setAttribute("href", link);
  if (
    ![location.host, new URL(env.api_url).host].includes(new URL(link).host)
  ) {
    a.setAttribute("target", "_blank");
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadResource = (url: string, filename?: string) => {
  //use cross origin when the current origin allowed
  if (!filename) filename = url.split("\\").pop()?.split("/").pop();
  fetch(url, {
    headers: new Headers({
      Origin: location.origin,
    }),
    mode: "cors",
  })
    .then((response) => response.blob())
    .then((blob) => {
      downloadBlob(blob, filename);
    })
    .catch((e) => console.error(e));
};

export const safeUrl = (src: string): string => {
  return src.replace(/<|>/g, "");
};

export const convertToBase64 = (obj: any) => {
  const json = JSON.stringify(
    obj,
    (_, val) =>
      (0 === val || (null === val ? "" : val)) &&
      ("object" === typeof val ? val : String(val))
  );

  return Buffer.from(json).toString("base64");
};

export const convertBase64ToBlob = (
  b64Data: string,
  contentType: string,
  sliceSize: number = 512
) => {
  const byteCharacters = atob(b64Data); //NOSONAR
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const getBlobUrlFromBase64 = (dataUrl: string) => {
  let contentType = dataUrl?.includes("image")
    ? "image/jpeg"
    : "application/pdf";
  return getBlobUrl(convertBase64ToBlob(dataUrl?.split(",")[1], contentType));
};

export const formatDate = (date: Date, format: string = "YYYYMMDD") => {
  return moment(date).format(format);
};

export function getDateXDaysAgo(numOfDays: number, date = new Date()) {
  const daysAgo = new Date(date.getTime());
  daysAgo.setDate(date.getDate() - numOfDays);
  return daysAgo;
}

export const getDatePeriodStr = (format: string = "DD/MM/YYYY") => {
  return {
    dateFrom: formatDate(getDateXDaysAgo(30), format),
    dateTo: formatDate(new Date(), format),
  };
};

export const formatPrice = (price: number) => {
  const result = Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(price);
  return result;
};

export const getLanguageFullName = (lang: string) => {
  let fullName;
  switch (lang) {
    case "en":
      fullName = "English";
      break;
    case "fr":
      fullName = "France";
      break;
    case "de":
      fullName = "German";
      break;
    case "it":
      fullName = "Italian";
      break;
    case "es":
      fullName = "Spanish";
      break;
    case "ru":
      fullName = "Russian";
      break;
    default:
      fullName = lang;
  }
  return fullName;
};

export const calculatePositionUpToDirection: (
  direction: ContextMenuDirections,
  openerRect: React.RefObject<any>,
  wrapperRect: React.RefObject<any>,
  isFixed: boolean,
  isSticky?: boolean
) => Position = (direction, refOpener, refWrapper, isFixed, isSticky) => {
  //NOSONAR
  let top, left;

  let openerRect = refOpener.current.getBoundingClientRect();
  let wrapperRect = refWrapper.current.getBoundingClientRect();
  let bodyRect = document.body.getBoundingClientRect();

  switch (direction) {
    case "top":
      top = openerRect.top - bodyRect.top - wrapperRect.height;
      if (top < 0) {
        top = openerRect.bottom - bodyRect.top;
        if (top + wrapperRect.height > bodyRect.height) {
          top = 0;
        }
      }
      left = (openerRect.right + openerRect.left) / 2 - wrapperRect.width / 2;
      if (left < 0) {
        left = 0;
      } else if (left + wrapperRect.width > bodyRect.right) {
        left = bodyRect.right - wrapperRect.width - 5;
      }
      break;
    case "bottom":
      top =
        openerRect.bottom -
        (isSticky ? (isFixed ? 0 : bodyRect.top) : bodyRect.top); //NOSONAR
      if (top + wrapperRect.height > bodyRect.height) {
        top = openerRect.top - bodyRect.top - wrapperRect.height;
        if (top < 0) {
          top = bodyRect.height - wrapperRect.height;
        }
      }
      left = (openerRect.right + openerRect.left) / 2 - wrapperRect.width / 2;
      if (left < 0) {
        left = 0;
      } else if (left + wrapperRect.width > bodyRect.right) {
        left = bodyRect.right - wrapperRect.width;
      }
      break;
    case "left":
      top =
        (openerRect.top + openerRect.bottom) / 2 -
        wrapperRect.height / 2 -
        bodyRect.top;
      if (top < 0) {
        top = 0;
      } else if (top + wrapperRect.height > bodyRect.height) {
        top = bodyRect.height - wrapperRect.height;
        if (top < 0) {
          top = 0;
        }
      }
      left = openerRect.left - wrapperRect.width;
      if (left < 0) {
        left = openerRect.right;
        if (left + wrapperRect.width > bodyRect.width) {
          left = 0;
        }
      }
      break;
    case "right":
      top =
        (openerRect.top + openerRect.bottom) / 2 -
        wrapperRect.height / 2 -
        bodyRect.top;
      if (top < 0) {
        top = 0;
      } else if (top + wrapperRect.height > bodyRect.height) {
        top = bodyRect.height - wrapperRect.height;
        if (top < 0) {
          top = 0;
        }
      }

      left = openerRect.right;
      if (left + wrapperRect.width > bodyRect.width) {
        left = openerRect.left - wrapperRect.width;
        if (left < 0) {
          left = bodyRect.width - wrapperRect.width;
        }
      }
      break;
    case "topLeft":
      top = openerRect.top - bodyRect.top - wrapperRect.height;
      if (top < 0) {
        top = openerRect.bottom - bodyRect.top;
        if (top + wrapperRect.height > bodyRect.height) {
          top = 0;
        }
      }
      left = openerRect.left;
      if (left + wrapperRect.width > bodyRect.width) {
        left = bodyRect.width - wrapperRect.width;
      }
      break;
    case "topRight":
      top = openerRect.top - bodyRect.top - wrapperRect.height;
      if (top < 0) {
        top = openerRect.bottom - bodyRect.top;
        if (top + wrapperRect.height > bodyRect.height) {
          top = 0;
        }
      }
      left = openerRect.right - wrapperRect.width;
      if (left < 0) {
        left = 0;
      }
      break;
    case "bottomLeft":
      top =
        openerRect.bottom -
        (isSticky ? (isFixed ? 0 : bodyRect.top) : bodyRect.top); //NOSONAR
      if (top + wrapperRect.height > bodyRect.height) {
        top = openerRect.top - bodyRect.top - wrapperRect.height;
        if (top < 0) {
          top = bodyRect.height - wrapperRect.height - 5;
        }
      }
      left = openerRect.left;
      if (left + wrapperRect.width > bodyRect.width) {
        left = bodyRect.width - wrapperRect.width;
      }
      break;
    case "bottomRight":
      top =
        openerRect.bottom -
        (isSticky ? (isFixed ? 0 : bodyRect.top) : bodyRect.top); //NOSONAR
      if (top + wrapperRect.height > bodyRect.height) {
        top = openerRect.top - bodyRect.top - wrapperRect.height;
        if (top < 0) {
          top = bodyRect.height - wrapperRect.height - 5;
        }
      }
      left = openerRect.right - wrapperRect.width;
      if (left < 0) {
        left = 0;
      }
      break;
  }
  return { top, left } as Position;
};

export const buildPath = (...args: string[]) => {
  return args.join("/").replace(/\/\/+/g, "/");
};

export const parsePermissions = (permissions: string[]) => {
  const result = {} as { [key: string]: string[] };
  permissions.forEach((p) => {
    const [key, value] = p.split(".");
    if (result[key]) {
      result[key].push(value);
    } else {
      result[key] = [value];
    }
  });

  return result;
};

export const capitalize = (s: string) => {
  const firstLetter = s[0].toUpperCase();

  return firstLetter + s.slice(1, s.length);
};

export const generateKey = () => {
  return uuidv4();
};

export const generateUniqueId = () => {
  return uuidv4();
};

export const generateId = () => {
  return uuidv4().replace("-", "");
};

export const changeUrlLanguage = (pathname: string, lang: string) => {
  const pathArr = pathname.split("/");
  pathArr[1] = lang;

  const newPath = pathArr.join("/");

  return newPath;
};

export const treeDataFromListData = (data: any) => {
  const mapFunction = (i: any) => {
    const result: any = {
      key: i.ID,
      title: i.Description,
      value: i.ID,
      inactive: i.Inactive,
    };
    if (i.children) {
      result.children = i.children.map(mapFunction);
    }

    return result;
  };
  return data.map(mapFunction);
};

export const getObjectProperty = (object: any, path: string) => {
  if (object == null) {
    // null or undefined
    return object;
  }
  const parts = path.split(".");
  for (const part of parts) {
    if (object == null) {
      // null or undefined
      return undefined;
    }
    const key = part;
    object = object[key];
  }
  return object;
};

export const getPathFromArray = (
  path?: Readonly<string | number | Array<string | number>>
) => {
  if (Array.isArray(path)) {
    return path.join(".");
  } else {
    return path;
  }
};

export const getKeyUpHandler: any = (
  callback?: Function,
  key?: string | string[] | KeyboardEvent
) => {
  return (e: KeyboardEvent) => {
    if (
      callback &&
      ((typeof key === "string" && e.key === key) ||
        (Array.isArray(key) && key.includes(e.key)))
    ) {
      callback();
    }
  };
};

export const computeComplementaryColor = (hexColor: string) => {
  // Convert hex to RGB
  // #ffcc11
  const rgb = hexToRgb(hexColor);

  // Compute complementary color
  const compR = 255 - rgb[0];
  const compG = 255 - rgb[1];
  const compB = 255 - rgb[2];

  // Convert back to hex
  return rgbToHex([compR, compG, compB]);
};

const hexToRgb = (hexColor: string) => {
  // Remove '#' if present
  hexColor = hexColor.replace("#", "");

  // Convert hex to RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  return [r, g, b];
};

export const getCorrectTextColor = (hex: string) => {
  /*
  From this W3C document: http://www.webmasterworld.com/r.cgi?f=88&d=9769&url=http://www.w3.org/TR/AERT#color-contrast
  
  Color brightness is determined by the following formula: 
  ((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
  
  I know this could be more compact, but I think this is easier to read/explain.
  
  */

  let threshold = 130; /* about half of 256. Lower threshold equals more dark text on dark background  */

  let hRed = hexToR(hex);
  let hGreen = hexToG(hex);
  let hBlue = hexToB(hex);

  function hexToR(h: string) {
    return parseInt(cutHex(h).substring(0, 2), 16);
  }
  function hexToG(h: string) {
    return parseInt(cutHex(h).substring(2, 4), 16);
  }
  function hexToB(h: string) {
    return parseInt(cutHex(h).substring(4, 6), 16);
  }
  function cutHex(h: string) {
    return h.charAt(0) == "#" ? h.substring(1, 7) : h;
  }

  let cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
  if (cBrightness > threshold) {
    return "#000000";
  } else {
    return "#ffffff";
  }
};

export const rgbToHex = (rgb: number[]) =>
  "#" + rgb.map((value) => ("0" + value.toString(16)).slice(-2)).join("");
